import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixInnovationAssistantWidgetV1MessagesUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/wix-assistant-widget',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/wix-assistant-widget',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/wix-assistant-widget',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/wix-assistant-widget',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/wix-assistant-widget',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/wix-assistant-widget',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/wix-assistant-widget',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/wix-assistant-widget',
        destPath: '',
      },
      {
        srcPath: '/innovation/widget',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/wix-assistant-widget',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ai-site-chat_messages';

/**
 * Bulk creates messages.
 *
 * This method must be called using the site visitor or site member identity ([SDK](https://dev.wix.com/docs/sdk/articles/get-started/about-identities#site-member) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/about-identities#site-member)) to identify the site visitor that is creating the messages.
 */
export function bulkCreateMessages(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateMessages({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'messages.createdDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.innovation.widget.v1.message',
      method: 'POST' as any,
      methodFqn:
        'wix.innovation.assistant.widget.v1.Messages.BulkCreateMessages',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInnovationAssistantWidgetV1MessagesUrl({
        protoPath: '/v1/bulk/messages/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'messages.createdDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateMessages;
}

/**
 * Retrieves a list of messages.
 *
 * Up to 100 Messages can be returned per request.
 *
 * This method must be called using the site visitor or site member identity ([SDK](https://dev.wix.com/docs/sdk/articles/get-started/about-identities#site-member) | [REST](https://dev.wix.com/docs/rest/articles/getting-started/about-identities#site-member)) to identify the site visitor that is retrieving the messages.
 */
export function listMessages(payload: object): RequestOptionsFactory<any> {
  function __listMessages({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.innovation.widget.v1.message',
      method: 'GET' as any,
      methodFqn: 'wix.innovation.assistant.widget.v1.Messages.ListMessages',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInnovationAssistantWidgetV1MessagesUrl({
        protoPath: '/v1/messages/list',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'messages.createdDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __listMessages;
}

/** Allows site visitor to create upload url to use for uploading a file. */
export function mediaAttachmentUploadUrl(
  payload: object
): RequestOptionsFactory<any> {
  function __mediaAttachmentUploadUrl({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.innovation.widget.v1.message',
      method: 'GET' as any,
      methodFqn:
        'wix.innovation.assistant.widget.v1.Messages.MediaAttachmentUploadUrl',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInnovationAssistantWidgetV1MessagesUrl({
        protoPath: '/v1/messages/files/generate-upload-url',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __mediaAttachmentUploadUrl;
}

/**
 * Retrieves messages by inbox conversation ID.
 *
 * Up to 100 Messages can be returned per request.
 */
export function bulkGetByInboxMessages(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkGetByInboxMessages({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.innovation.widget.v1.message',
      method: 'GET' as any,
      methodFqn:
        'wix.innovation.assistant.widget.v1.Messages.BulkGetByInboxMessages',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixInnovationAssistantWidgetV1MessagesUrl({
        protoPath: '/v1/messages/get-by-inbox',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'results.message.createdDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __bulkGetByInboxMessages;
}
